import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Mail, Phone, MapPin, CheckCircle2, FileText } from "lucide-react";

const CATEGORIES = [
  "Product Quality",
  "Order / Delivery",
  "Refund / Return",
  "Payout / Commission",
  "IBA Conduct",
  "Website / Technical",
  "General",
];

export default function Grievance() {
  const [officer, setOfficer] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", user_code: "",
    category: "General", subject: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    api.get("/grievance/officer").then((r) => setOfficer(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/grievance", form);
      setSubmitted(data);
      toast.success(`Ticket ${data.ticket_no} filed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit grievance");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand">
      <Navbar />
      <section className="bg-navy text-white py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.4em] text-gold mb-4">Direct Seller Grievance</div>
          <h1 className="font-serif text-4xl md:text-5xl">Redressal Cell</h1>
          <p className="mt-4 text-white/70 max-w-2xl leading-relaxed">
            In compliance with the Consumer Protection (Direct Selling) Rules, 2021, any customer, IBA, or franchise partner may file a grievance below. Our Redressal Officer will respond within <b className="text-gold">30 days</b>.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Officer info */}
        <aside className="lg:col-span-1 space-y-4" data-testid="grievance-officer">
          <div className="bg-white border border-navy/10 rounded-sm p-6">
            <div className="text-xs uppercase tracking-widest text-gold mb-1">Grievance Redressal Officer</div>
            <div className="font-serif text-lg text-navy">{officer?.name || "—"}</div>
            <div className="text-xs text-navy/50 mt-1">{officer?.company}</div>
            <div className="mt-5 space-y-3 text-sm">
              <div className="flex items-start gap-2"><Mail size={14} className="text-gold-deep mt-1" /><a href={`mailto:${officer?.email}`} className="text-navy hover:text-gold">{officer?.email || "—"}</a></div>
              <div className="flex items-start gap-2"><Phone size={14} className="text-gold-deep mt-1" /><a href={`tel:${officer?.phone}`} className="text-navy hover:text-gold">{officer?.phone || "—"}</a></div>
              <div className="flex items-start gap-2"><MapPin size={14} className="text-gold-deep mt-1" /><span className="text-navy/70">{officer?.address || "—"}</span></div>
            </div>
            <div className="mt-5 pt-4 border-t border-navy/10 text-[11px] leading-relaxed text-navy/60">
              <ShieldCheck size={14} className="inline text-olive mr-1" />
              {officer?.rules_citation}
            </div>
          </div>
          <div className="bg-cream/60 border border-gold/20 rounded-sm p-5 text-xs text-navy/70 leading-relaxed">
            <FileText size={14} className="inline text-gold-deep mr-1" />
            You will receive a ticket number after submission. Save it for follow-up.
          </div>
        </aside>

        {/* Form */}
        <div className="lg:col-span-2">
          {submitted ? (
            <div className="bg-white border border-olive/20 rounded-sm p-10 text-center" data-testid="grievance-success">
              <CheckCircle2 size={40} className="text-olive mx-auto" />
              <h2 className="font-serif text-2xl text-navy mt-3">Grievance filed</h2>
              <div className="text-sm text-navy/60 mt-2">{submitted.message}</div>
              <div className="mt-4 inline-block px-4 py-2 bg-cream border border-gold/30 rounded-sm">
                <span className="text-xs uppercase tracking-widest text-navy/50 mr-2">Ticket</span>
                <span className="font-mono text-gold-deep font-semibold" data-testid="ticket-no">{submitted.ticket_no}</span>
              </div>
              <button
                onClick={() => { setSubmitted(null); setForm({ name: "", email: "", phone: "", user_code: "", category: "General", subject: "", message: "" }); }}
                className="block mx-auto mt-6 text-xs uppercase tracking-widest text-navy/60 hover:text-gold"
              >
                File another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-navy/10 rounded-sm p-6 md:p-8 space-y-4" data-testid="grievance-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full name *"><input required data-testid="gv-name" value={form.name} onChange={(e) => update("name", e.target.value)} className={IN} /></Field>
                <Field label="Phone *"><input required data-testid="gv-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={IN} /></Field>
                <Field label="Email"><input type="email" data-testid="gv-email" value={form.email} onChange={(e) => update("email", e.target.value)} className={IN} /></Field>
                <Field label="IBA / Order Code (if applicable)"><input data-testid="gv-code" value={form.user_code} onChange={(e) => update("user_code", e.target.value)} className={IN} /></Field>
                <Field label="Category">
                  <select data-testid="gv-category" value={form.category} onChange={(e) => update("category", e.target.value)} className={IN}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Subject"><input data-testid="gv-subject" value={form.subject} onChange={(e) => update("subject", e.target.value)} className={IN} placeholder="One-line summary" /></Field>
              </div>
              <Field label="Complaint / message *">
                <textarea required rows={6} data-testid="gv-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Describe the issue in detail — dates, product/order codes, screenshots reference (min 15 chars)" className={`${IN} resize-none`} />
              </Field>
              <div className="flex items-center justify-between pt-2">
                <div className="text-[11px] text-navy/50">By submitting, you agree that Samara may contact you at the phone/email above for resolution.</div>
                <button type="submit" disabled={busy} data-testid="gv-submit" className="bg-navy text-white px-6 py-3 hover:bg-navy-deep rounded-sm disabled:opacity-50 text-sm uppercase tracking-widest">
                  {busy ? "Filing…" : "File grievance"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest text-navy/50 mb-1">{label}</div>
      {children}
    </label>
  );
}

// Tailwind class shared by all form inputs on this page
const IN = "w-full border border-navy/15 focus:border-gold focus:outline-none rounded-sm px-3 py-2 text-sm bg-white";
